import React, { useRef } from "react";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import useQuestionSectionStyles from "../testStyle/iitStyle/questionSectionStyle";
import { useLanguages } from "../../../context/LanguagesContext";
import ControlButtons from "./ControlButtons";
import { useQuestionSection } from "../../../hooks/iit/useQuestionSection";
import QuestionRenderer from "./QuestionRenderer";
import SubmissionPopupReLogin from "../../../components/popups/SubmissionPopupReLogin";
import SubmissionPopup from "../../../components/popups/SubmissionPopup";
import ServerErrorDialog from "../../../components/popups/ServerErrorDialog";
import FinalSubmissionDialog from "../../../components/popups/FinalSubmissionDialog";
import down from "../../../images/down.png";
import up from "../../../images/up.png";
import Loader from "../../../components/Loader";

const QuestionSection = ({
  questionData,
  currentQuestion,
  setCurrentQuestion,
  subjectId,
}) => {
  const classes = useQuestionSectionStyles();
  const { selectedLanguage } = useLanguages();
  const question = questionData ? questionData[currentQuestion] : {};
  const paperRef = useRef(null);

  const {
    selectedOptionId,
    selectedOptionIds,
    inputAnswer,
    showSubmissionPopup,
    showSubmissionPopupReLogin,
    showServerErrorDialog,
    setShowServerErrorDialog,
    showSubmitModal,
    isTimeUp,
    countdownTime,
    finalSubmitChecked,
    submitError,
    countdownByHead,
    stats,
    handleOptionChange,
    handleMultipleChoiceChange,
    handleInputChange,
    resetSelections,
    handleSaveNext,
    handleMarkReview,
    handleClearResponse,
    handleBack,
    handleNext,
    handleOpenSubmitModal,
    handleCloseSubmitModal,
    handleFinalSubmitCheck,
    handleFinalSubmit,
    handleNextIit,
    handleBackIit
  } = useQuestionSection({
    questionData,
    currentQuestion,
    setCurrentQuestion,
    subjectId,
  });

  const scrollToBottom = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: paperRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const scrollToTop = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <Box className={classes.root}>
        <Paper className={classes.paper} ref={paperRef}>
          <div className={classes.questionContainer}>
            <Typography variant="h6" className={classes.questionTitle}>
              Question {currentQuestion + 1}:
              <IconButton onClick={scrollToBottom} size="small">
                <img
                  src={down}
                  alt="Scroll down"
                  style={{
                    width: 24,
                    height: 24,
                    "@media (min-width: 800px) and (max-width: 898px)": {
                      width: "16.8px", // 70% of 24px
                      height: "16.8px", // 70% of 24px
                    },
                    "@media (min-width: 992px)": {
                        fontSize: "0.7rem", // 70% font size
                      },
                  }}
                />
              </IconButton>
            </Typography>

            {(question?.paragraph_en || question?.paragraph_hi) && (
              <>
                <Box className={classes.contentContainer}>
                  <Typography
                    variant="h6"
                    style={{
                      paddingTop: "5px",
                      paddingBottom: "5px",
                      "@media (min-width: 800px) and (max-width: 898px)": {
                        fontSize: "0.7rem", // 70% font size
                      },
                      "@media (min-width: 992px)": {
                        fontSize: "0.7rem", // 70% font size
                      },
                    }}
                  >
                    Paragraph
                  </Typography>
                  <span
                    dangerouslySetInnerHTML={{
                      __html:
                        selectedLanguage === "english"
                          ? question?.paragraph_en
                          : question?.paragraph_hi,
                    }}
                    style={{
                      "@media (min-width: 800px) and (max-width: 898px)": {
                        fontSize: "0.7rem", // 70% font size
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {questionData ? (
              <>
                <Typography
                  variant="body1"
                  gutterBottom
                  style={{
                    marginTop: "16px",
                    "@media (min-width: 800px) and (max-width: 898px)": {
                      fontSize: "0.7rem", // 70% font size
                    },
                  }}
                >
                  {selectedLanguage === "english" ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: question?.question_en,
                      }}
                    />
                  ) : (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: question?.question_hi,
                      }}
                    />
                  )}
                </Typography>
                {(question?.description_en || question?.description_hi) && (
                  <div
                    style={{
                      paddingTop: "10px",
                      paddingBottom: "10px",
                      "@media (min-width: 800px) and (max-width: 898px)": {
                        fontSize: "0.7rem", // 70% font size
                      },
                    }}
                  >
                    Description:{" "}
                    <div
                      style={{
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        "@media (min-width: 800px) and (max-width: 898px)": {
                          fontSize: "0.7rem", // 70% font size
                        },
                      }}
                    ></div>
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedLanguage === "english"
                            ? question?.description_en
                            : question?.description_hi,
                      }}
                    />
                  </div>
                )}
                <QuestionRenderer
                  question={question}
                  selectedLanguage={selectedLanguage}
                  selectedOptionId={selectedOptionId}
                  selectedOptionIds={selectedOptionIds}
                  inputAnswer={inputAnswer}
                  handleOptionChange={handleOptionChange}
                  handleMultipleChoiceChange={handleMultipleChoiceChange}
                  handleInputChange={handleInputChange}
                />
              </>
            ) : (
              <Loader />
            )}

            <div className={classes.upWrapper}>
              <IconButton onClick={scrollToTop} size="small">
                <img
                  src={up}
                  alt="Scroll up"
                  style={{
                    width: 24,
                    height: 24,
                    "@media (min-width: 800px) and (max-width: 898px)": {
                      width: "16.8px", // 70% of 24px
                      height: "16.8px", // 70% of 24px
                    },
                    "@media (min-width: 992px)": {
                      width: "16.8px", // 70% of 24px
                      height: "16.8px", // 70% of 24px
                    },
                  }}
                />
              </IconButton>
            </div>
          </div>
        </Paper>
      </Box>

      <ControlButtons
        handleSaveNext={handleSaveNext}
        handleMarkReview={handleMarkReview}
        handleSubmit={handleOpenSubmitModal}
        handleClearResponse={handleClearResponse}
        currentQuestion={currentQuestion}
        questionData={questionData}
        handleBack={handleBackIit}
        handleNext={handleNextIit}
      />

      <SubmissionPopupReLogin
        open={showSubmissionPopupReLogin}
        countdown={countdownByHead}
      />

      <SubmissionPopup
        open={showSubmissionPopup}
        countdown={countdownByHead}
        stats={stats}
      />

      <ServerErrorDialog
        open={showServerErrorDialog}
        onClose={() => setShowServerErrorDialog(false)}
      />

      <FinalSubmissionDialog
        open={showSubmitModal || isTimeUp}
        onClose={handleCloseSubmitModal}
        onSubmit={handleFinalSubmit}
        isTimeUp={isTimeUp}
        stats={stats}
        submitError={submitError}
        countdownTime={countdownTime}
        finalSubmitChecked={finalSubmitChecked}
        onFinalSubmitCheck={handleFinalSubmitCheck}
      />
    </>
  );
};

export default QuestionSection;
